import React, { useImperativeHandle, useRef } from 'react';
import { VisuallyHidden } from '~/shared/components/VisuallyHidden';
import { useInputField } from '../../hooks/useInputField';
import {
    StyledToggleSwitch,
    StyledToggleSwitchInput,
    StyledToggleSwitchWrapper,
    StyledToggleSwitchContainer,
    StyledToggleSwitchOutline,
    StyledLabel,
} from './styled';

type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

export type ToggleSwitchProps = Omit<InputProps, 'placeholder' | 'value'> & {
    /**
     * Adds a label to the input field. This is required for accessibilty.
     */
    label: string | React.ReactNode;
    /**
     * Visually hides label and gap.
     */
    visuallyHideLabel?: boolean;
};

export const ToggleSwitch = React.forwardRef<HTMLInputElement, ToggleSwitchProps>(
    ({ label, visuallyHideLabel, id, onChange, disabled, ...rest }, ref) => {
        const inputRef = useRef<HTMLInputElement>(null);

        const { fieldId, describedById } = useInputField({ id });

        const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
            onChange && onChange(event);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
        useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, [ref]);

        return (
            <StyledToggleSwitchContainer
                key={fieldId}
                disabled={disabled}
                hasGap={!visuallyHideLabel}
            >
                <StyledToggleSwitchWrapper>
                    <StyledToggleSwitchInput
                        type="checkbox"
                        id={fieldId}
                        ref={inputRef}
                        aria-describedby={describedById}
                        onChange={onChangeHandler}
                        disabled={disabled}
                        {...rest}
                    />
                    <StyledToggleSwitch />
                    <StyledToggleSwitchOutline />
                </StyledToggleSwitchWrapper>
                <StyledLabel
                    id={describedById}
                    as="label"
                    variant="bodySm"
                    htmlFor={fieldId}
                    title={typeof label === 'string' ? label : undefined}
                    children={visuallyHideLabel ? <VisuallyHidden>{label}</VisuallyHidden> : label}
                />
            </StyledToggleSwitchContainer>
        );
    }
);
